import React from "react";
import PropTypes from "prop-types";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import TimerOffOutlinedIcon from "@mui/icons-material/TimerOffOutlined";
import { Button, IconButton } from "@mui/material";
import { NodeDateTimePicker, NodeTextField } from "../../NodeUI";
import ToggleRow from "../../common/ToggleRow";
import MissionWakeTriggerCard from "../agentConfig/tabs/mission/MissionWakeTriggerCard";
import MissionReadOnlyDurationField from "../agentConfig/tabs/mission/MissionReadOnlyDurationField";
import "../agentConfig/tabs/mission/MissionHarness.css";

export default function MissionPolicyWakeFields({ draft, onChange, readOnly = false }) {
  const set = (key, value) => {
    if (readOnly) return;
    onChange(key, value);
  };

  const updateScheduledWake = (index, field, value) => {
    set(
      "scheduledWakes",
      (draft.scheduledWakes || []).map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row
      )
    );
  };

  const addScheduledWake = () => {
    set("scheduledWakes", [...(draft.scheduledWakes || []), { at: "", message: "" }]);
  };

  const removeScheduledWake = (index) => {
    set(
      "scheduledWakes",
      (draft.scheduledWakes || []).filter((_, rowIndex) => rowIndex !== index)
    );
  };

  const enableScheduledWakes = () => {
    if ((draft.scheduledWakes || []).length === 0) {
      addScheduledWake();
      return;
    }
    set("scheduledWakes", draft.scheduledWakes);
  };

  return (
    <div className="mission-harness__wake-list">
      <MissionWakeTriggerCard
        icon={ScheduleOutlinedIcon}
        title="Heartbeat"
        description="Recurring wake on a fixed cadence."
        enabled={draft.heartbeatEnabled === true}
        switchDisabled={readOnly}
        onEnabledChange={(value) => set("heartbeatEnabled", value)}
      >
        <MissionReadOnlyDurationField
          label="Every"
          isoValue={draft.cadence}
          fallbackUnit="hours"
          disabled={readOnly}
          inDialog
          onChange={(iso) => set("cadence", iso)}
        />
        <div className="mission-harness__field-row">
          <NodeTextField
            label="Timezone"
            value={draft.heartbeatTimezone}
            onChange={(value) => set("heartbeatTimezone", value)}
            disabled={readOnly}
            placeholder="e.g. America/New_York"
          />
          <NodeTextField
            label="Time of day"
            value={draft.heartbeatTimeOfDay}
            onChange={(value) => set("heartbeatTimeOfDay", value)}
            disabled={readOnly}
            placeholder="e.g. 09:00"
          />
        </div>
        <NodeTextField
          label="Days of week"
          value={draft.heartbeatDaysOfWeek}
          onChange={(value) => set("heartbeatDaysOfWeek", value)}
          disabled={readOnly}
          placeholder="1=Mon … 7=Sun, comma-separated. Empty = every day."
        />
        <NodeTextField
          label="Wake message"
          value={draft.message}
          onChange={(value) => set("message", value)}
          multiline
          minRows={2}
          maxRows={4}
          disabled={readOnly}
        />
      </MissionWakeTriggerCard>

      <MissionWakeTriggerCard
        icon={EventOutlinedIcon}
        title="Scheduled checks"
        description="Wake once at a specific time. Tell the agent why — e.g. review Q1 progress or chase overdue items."
        enabled={(draft.scheduledWakes || []).length > 0}
        switchDisabled={readOnly}
        onEnabledChange={(enabled) => {
          if (enabled) {
            enableScheduledWakes();
            return;
          }
          set("scheduledWakes", []);
        }}
      >
        {(draft.scheduledWakes || []).map((row, index) => (
          <div key={`scheduled-${index}`} className="mission-harness__scheduled-check">
            <div className="mission-harness__scheduled-check-at-row">
              <NodeDateTimePicker
                label="At"
                value={row.at || ""}
                onChange={(value) => updateScheduledWake(index, "at", value)}
                disabled={readOnly}
                timezone="UTC"
                placeholder="Select date and time"
              />
              {!readOnly ? (
                <IconButton
                  size="small"
                  aria-label="Remove scheduled check"
                  className="mission-harness__scheduled-check-delete"
                  onClick={() => removeScheduledWake(index)}
                >
                  <DeleteOutlineOutlinedIcon fontSize="small" />
                </IconButton>
              ) : null}
            </div>
            <NodeTextField
              label="Wake message"
              value={row.message}
              onChange={(value) => updateScheduledWake(index, "message", value)}
              disabled={readOnly}
              multiline
              minRows={2}
              maxRows={3}
              placeholder="What should the agent do at this wake?"
              helperText="Leave blank to use the turn policy wake message."
            />
          </div>
        ))}
        {!readOnly ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddOutlinedIcon />}
            onClick={addScheduledWake}
            className="mission-harness__list-add"
          >
            Add scheduled check
          </Button>
        ) : null}
      </MissionWakeTriggerCard>

      <MissionWakeTriggerCard
        icon={TimerOffOutlinedIcon}
        title="No-progress deadline"
        description="Wake when the subject has made no progress for too long."
        enabled={draft.noProgressEnabled === true}
        switchDisabled={readOnly}
        onEnabledChange={(value) => {
          set("noProgressEnabled", value);
          if (value && !draft.noProgressDeadline) {
            set("noProgressDeadline", "P7D");
          }
        }}
      >
        <MissionReadOnlyDurationField
          label="After"
          isoValue={draft.noProgressDeadline}
          fallbackUnit="days"
          disabled={readOnly}
          inDialog
          onChange={(iso) => set("noProgressDeadline", iso)}
        />
        <p className="mission-harness__helper">
          Uses the turn policy wake message when the watchdog fires.
        </p>
      </MissionWakeTriggerCard>

      <div className="mission-harness__field-row mission-harness__field-row--3">
        <NodeTextField
          label="Debounce (ms)"
          type="number"
          value={draft.debounceMs}
          onChange={(value) => set("debounceMs", value)}
          inputProps={{ min: 0 }}
          disabled={readOnly}
          helperText="Delay before a pending wake fires."
        />
        <NodeTextField
          label="Cooldown (ms)"
          type="number"
          value={draft.cooldownMs}
          onChange={(value) => set("cooldownMs", value)}
          inputProps={{ min: 0 }}
          disabled={readOnly}
          helperText="Minimum spacing between turns from wakes."
        />
        <NodeTextField
          label="Max turns per hour"
          type="number"
          value={draft.maxTurnsPerHour}
          onChange={(value) => set("maxTurnsPerHour", value)}
          inputProps={{ min: 1 }}
          disabled={readOnly}
          helperText="Rate limit on wakes that dispatch a turn."
        />
      </div>

      <ToggleRow
        checked={draft.skipIfSubjectRunning !== false}
        onChange={(value) => set("skipIfSubjectRunning", value)}
        title="Skip wake if subject running"
        description="Do not dispatch a turn while the subject session is already running."
      />
    </div>
  );
}

MissionPolicyWakeFields.propTypes = {
  draft: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};
